import React, { useEffect, useMemo, useState } from 'react';
import { Boxes, ListOrdered, Package, Rows3 } from 'lucide-react';
import { apiService } from '../../services/api';
import { dayLabel, money, projectDot, qty, SearchBox, Spinner } from './ui';

// Achats par projet : ce qui a été acheté (référence, quantité, montant HT frais de port compris), regroupé par
// référence ou dans l'ordre des factures ; les factures anciennes sans lignes restent listées à part.

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const sumByCurrency = (rows, key = 'amount') => {
  const totals = {};
  rows.forEach((r) => { totals[r.currency || '€'] = (totals[r.currency || '€'] || 0) + (Number(r[key]) || 0); });
  return Object.entries(totals).map(([currency, amount]) => money(amount, currency)).join(' + ') || money(0);
};

const ItemsTable = ({ items, showProject = false, onOpenInvoice }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="text-left text-[11px] uppercase tracking-wide text-gray-500">
        <tr className="border-b border-gray-100">
          <th className="py-1.5 pr-3 font-medium">Date</th>
          <th className="py-1.5 pr-3 font-medium">Article</th>
          {showProject && <th className="py-1.5 pr-3 font-medium">Projet</th>}
          <th className="py-1.5 pr-3 text-right font-medium">Qté</th>
          <th className="py-1.5 pr-3 text-right font-medium">Montant HT</th>
          <th className="py-1.5 font-medium">Fournisseur</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {items.map((item) => (
          <tr key={item.ref} className="align-top hover:bg-gray-50">
            <td className="whitespace-nowrap py-1.5 pr-3 text-xs tabular-nums text-gray-500">{dayLabel(item.date)}</td>
            <td className="py-1.5 pr-3">
              <span className="text-gray-800">{item.designation}</span>
              {(item.mfr_reference || item.reference) && <span className="ml-2 font-mono text-[11px] text-gray-500">{item.mfr_reference || item.reference}</span>}
              {item.iteration_name && <span className="ml-2 text-[11px] text-gray-400">{item.iteration_name}</span>}
            </td>
            {showProject && (
              <td className="py-1.5 pr-3 text-xs">
                <span className="inline-flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${projectDot(item.project_id)}`} />{item.project_name}</span>
              </td>
            )}
            <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-gray-700" title={item.approximate ? 'Réparti au prorata (ancienne facture sur plusieurs projets)' : undefined}>
              {item.approximate && '≈ '}{qty(item.quantity)}
            </td>
            <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-gray-900">{money(item.amount, item.currency)}</td>
            <td className="py-1.5 text-xs text-gray-500">
              <button type="button" onClick={() => onOpenInvoice(item.expense_id)} className="text-left hover:text-blue-700 hover:underline">
                {item.vendor}{item.buyer_name && ` · ${item.buyer_name}`}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ReferenceTable = ({ groups }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="text-left text-[11px] uppercase tracking-wide text-gray-500">
        <tr className="border-b border-gray-100">
          <th className="py-1.5 pr-3 font-medium">Article</th>
          <th className="py-1.5 pr-3 text-right font-medium">Qté</th>
          <th className="py-1.5 pr-3 text-right font-medium">Cmdes</th>
          <th className="py-1.5 pr-3 font-medium">Dernier</th>
          <th className="py-1.5 pr-3 text-right font-medium">Montant HT</th>
          <th className="py-1.5 font-medium">Fournisseurs</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {groups.map((g) => (
          <tr key={`${g.reference}-${g.designation}`} className="align-top hover:bg-gray-50">
            <td className="py-1.5 pr-3">
              <span className="text-gray-800">{g.designation}</span>
              {g.reference && <span className="ml-2 font-mono text-[11px] text-gray-500">{g.reference}</span>}
            </td>
            <td className="whitespace-nowrap py-1.5 pr-3 text-right font-medium tabular-nums text-gray-900">{g.approximate && '≈ '}{qty(g.quantity)}</td>
            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500">{g.orders}</td>
            <td className="whitespace-nowrap py-1.5 pr-3 text-xs tabular-nums text-gray-500">{dayLabel(g.last_date)}</td>
            <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums text-gray-900">{money(g.amount, g.currency)}</td>
            <td className="py-1.5 text-xs text-gray-500">{g.vendors.join(', ')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ProjectPurchasesPanel = ({ projectId, title, onOpenInvoice }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('references');
  const [filter, setFilter] = useState('');
  const q = useDebounced(filter);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    apiService.get(`/purchases/projects/${projectId}${q ? `?q=${encodeURIComponent(q)}` : ''}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [projectId, q]);

  const legacyTotal = useMemo(() => (data ? sumByCurrency(data.legacy) : ''), [data]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
        {data && (
          <p className="text-xs text-gray-500">
            {data.totals.items} article{data.totals.items > 1 ? 's' : ''} sur {data.totals.invoices} facture{data.totals.invoices > 1 ? 's' : ''} · {sumByCurrency(data.items)} HT
          </p>
        )}
        <div className="ml-auto flex items-center gap-2">
          <SearchBox value={filter} onChange={setFilter} placeholder="Filtrer (réf., article, fournisseur)" className="w-56" />
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
            <button type="button" onClick={() => setView('references')} title="Par référence"
              className={`rounded-md p-1 ${view === 'references' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-700'}`}><Boxes className="h-4 w-4" /></button>
            <button type="button" onClick={() => setView('items')} title="Par date"
              className={`rounded-md p-1 ${view === 'items' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-700'}`}><ListOrdered className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      {loading && !data && <Spinner />}
      {data && (data.items.length ? (
        view === 'references' ? <ReferenceTable groups={data.by_reference} />
          : <ItemsTable items={data.items} onOpenInvoice={onOpenInvoice} />
      ) : <p className="text-sm text-gray-500">Aucun article détaillé{q ? ' pour ce filtre' : ''}.</p>)}
      {data?.legacy?.length > 0 && (
        <details className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2">
          <summary className="cursor-pointer text-sm text-gray-700">
            <Rows3 className="mr-1.5 inline h-4 w-4 text-gray-400" />
            {data.legacy.length} facture{data.legacy.length > 1 ? 's' : ''} sans détail des lignes · {legacyTotal} HT (part du projet)
          </summary>
          <ul className="mt-2 divide-y divide-gray-100 text-sm">
            {data.legacy.map((row) => (
              <li key={row.id} className="flex items-center gap-3 py-1">
                <span className="w-14 text-xs tabular-nums text-gray-500">{dayLabel(row.date)}</span>
                <button type="button" onClick={() => onOpenInvoice(row.id)} className="min-w-0 flex-1 truncate text-left text-gray-700 hover:text-blue-700 hover:underline">
                  {row.vendor} — {row.description}
                </button>
                <span className="tabular-nums text-gray-900">{money(row.amount, row.currency)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const ProjectPurchases = ({ search, onOpenInvoice }) => {
  const [projects, setProjects] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('');
  const [results, setResults] = useState(null);
  const q = useDebounced(search);

  useEffect(() => {
    apiService.get('/purchases/projects').then((rows) => {
      setProjects(rows || []);
      setSelected((current) => current || rows?.find((r) => r.status === 'in_progress')?.project_id || rows?.[0]?.project_id || null);
    }).catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults(null); return; }
    apiService.get(`/purchases/search?q=${encodeURIComponent(q)}`).then((data) => setResults(data.items || [])).catch(() => setResults([]));
  }, [q]);

  const shown = useMemo(() => (projects || []).filter((p) => !filter || `${p.project_name} ${p.client_name || ''}`.toLowerCase().includes(filter.toLowerCase())), [projects, filter]);
  const current = (projects || []).find((p) => p.project_id === selected);

  if (results) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-2 text-sm font-semibold text-gray-700">
          <Package className="mr-1.5 inline h-4 w-4 text-gray-400" />
          {results.length} achat{results.length > 1 ? 's' : ''} pour « {q} », tous projets
        </h2>
        {results.length ? <ItemsTable items={results} showProject onOpenInvoice={onOpenInvoice} />
          : <p className="text-sm text-gray-500">Rien trouvé dans les lignes des factures.</p>}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[17rem_1fr]">
      <aside className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto">
        <SearchBox value={filter} onChange={setFilter} placeholder="Projet…" className="mb-2" />
        {!projects && <Spinner />}
        <ul className="space-y-0.5">
          {shown.map((p) => (
            <li key={p.project_id}>
              <button type="button" onClick={() => setSelected(p.project_id)}
                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left ${selected === p.project_id ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'}`}>
                <span className={`h-2 w-2 shrink-0 rounded-full ${projectDot(p.project_id)}`} />
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-sm ${p.status === 'in_progress' ? 'text-gray-900' : 'text-gray-500'}`}>{p.project_name}</span>
                  <span className="block truncate text-[11px] text-gray-400">{p.client_name || '—'} · {dayLabel(p.last_date)}</span>
                </span>
                <span className="shrink-0 text-xs tabular-nums text-gray-600">{money(p.amount)}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {current ? <ProjectPurchasesPanel projectId={current.project_id} title={current.project_name} onOpenInvoice={onOpenInvoice} />
          : <p className="text-sm text-gray-500">Choisis un projet.</p>}
      </section>
    </div>
  );
};

export default ProjectPurchases;
